import { useEffect, useMemo, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'

import { ActionSelections, SelectionOption } from '../../../core/types/chat.types'
import FlowActionInlineForm from './flow-action-inline-form'

type FlowActionMonthYearFormProps = {
  title: string
  initialMonth?: number
  initialYear?: number
  onCancel: () => void
  onApply: (selections: ActionSelections) => void
}

const monthOptions: SelectionOption[] = [
  { code: '1', description: 'January' },
  { code: '2', description: 'February' },
  { code: '3', description: 'March' },
  { code: '4', description: 'April' },
  { code: '5', description: 'May' },
  { code: '6', description: 'June' },
  { code: '7', description: 'July' },
  { code: '8', description: 'August' },
  { code: '9', description: 'September' },
  { code: '10', description: 'October' },
  { code: '11', description: 'November' },
  { code: '12', description: 'December' },
]

const isValidMonth = (value?: number): boolean => typeof value === 'number' && value >= 1 && value <= 12
const isValidYear = (value?: number): boolean => typeof value === 'number' && value >= 2000 && value <= 2100

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--ds-fg-secondary, #535b6c)',
  },
  control: {
    width: '100%',
    borderRadius: 10,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    color: 'var(--ds-fg-primary, #1f2430)',
    padding: theme.spacing(1.25, 1.5),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    outline: 'none',
    boxSizing: 'border-box',
  },
}))

const FlowActionMonthYearForm = ({
  title,
  initialMonth,
  initialYear,
  onCancel,
  onApply,
}: FlowActionMonthYearFormProps) => {
  const classes = useStyles()
  const [selectedMonthCode, setSelectedMonthCode] = useState('')
  const [selectedYear, setSelectedYear] = useState('')

  useEffect(() => {
    setSelectedMonthCode(isValidMonth(initialMonth) ? String(initialMonth) : '')
    setSelectedYear(isValidYear(initialYear) ? String(initialYear) : '')
  }, [initialMonth, initialYear])

  const monthChosen = useMemo(
    () => (selectedMonthCode ? Number.parseInt(selectedMonthCode, 10) : undefined),
    [selectedMonthCode]
  )
  const yearChosen = useMemo(() => {
    const parsed = Number.parseInt(selectedYear, 10)
    return Number.isNaN(parsed) ? undefined : parsed
  }, [selectedYear])

  const canApply = useMemo(() => {
    const monthValid = monthChosen === undefined || isValidMonth(monthChosen)
    return monthValid && isValidYear(yearChosen)
  }, [monthChosen, yearChosen])

  const handleApply = () => {
    if (!canApply || yearChosen === undefined) {
      return
    }

    onApply({
      yearChosen,
      ...(monthChosen !== undefined ? { monthChosen } : {}),
    })
  }

  return (
    <FlowActionInlineForm title={title} canApply={canApply} onCancel={onCancel} onApply={handleApply}>
      <div className={classes.grid}>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="flow-month">
            Month (optional)
          </label>
          <select
            id="flow-month"
            className={classes.control}
            value={selectedMonthCode}
            onChange={(event) => setSelectedMonthCode(event.target.value)}
          >
            <option value="">Select month</option>
            {monthOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.description}
              </option>
            ))}
          </select>
        </div>

        <div className={classes.field}>
          <label className={classes.label} htmlFor="flow-year">
            Year
          </label>
          <input
            id="flow-year"
            className={classes.control}
            type="number"
            min={2000}
            max={2100}
            placeholder="YYYY"
            value={selectedYear}
            onChange={(event) => setSelectedYear(event.target.value)}
          />
        </div>
      </div>
    </FlowActionInlineForm>
  )
}

export default FlowActionMonthYearForm
